import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, useLanguage, useTheme, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowRight,
  Check,
  Sun,
  Moon,
  Globe,
  Sparkles,
  ChevronRight,
  Star,
  Zap,
  Play,
  Download,
  Clock,
  AlertTriangle,
  CircleDollarSign,
  BarChart3,
  CalendarCheck,
  Repeat,
  TrendingUp,
  ArrowUpRight,
  MessageSquare,
  CreditCard,
  Users,
  Wallet,
  BrainCircuit,
  Mail,
  Phone,
  Loader2,
  Send,
  CheckCircle2,
  Crown,
  Stethoscope,
  ClipboardList,
  Shield
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const SECTOR_OPTIONS = [
  { value: "dentisti", label_it: "Studio Dentistico", label_en: "Dental Practice" },
  { value: "estetica", label_it: "Centro Estetico", label_en: "Beauty Center" },
  { value: "fisioterapia", label_it: "Fisioterapia", label_en: "Physiotherapy" },
  { value: "medico", label_it: "Studio Medico", label_en: "Medical Practice" },
  { value: "consulente", label_it: "Consulente / Coach", label_en: "Consultant / Coach" },
  { value: "fitness", label_it: "Palestra / Personal Trainer", label_en: "Gym / Personal Trainer" },
  { value: "altro", label_it: "Altro", label_en: "Other" },
];

const VerticalLanding = ({ sector }) => {
  const { user } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", sector: sector.sectorKey || "", message: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [sessionId] = useState(() => `vl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const s = sector[language] || sector.it;
  // Landing key aligned to the public URL slug (so the analytics tab uses URL keys consistently)
  const SECTOR_TO_URL = { barbiere: "hair", dentisti: "dentisti", estetica: "estetica", fisioterapia: "benessere-posturale" };
  const landingKey = SECTOR_TO_URL[sector.sectorKey] || sector.sectorKey || "vertical";

  useEffect(() => {
    axios.post(`${API}/public/landing/track`, {
      event_type: "page_view", landing: landingKey, session_id: sessionId,
      meta: { ref: document.referrer || "" },
    }).catch(() => {});
    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight * 100;
      if (pct >= 50 && !window.__vl_s50) { window.__vl_s50 = true;
        axios.post(`${API}/public/landing/track`, { event_type: "scroll_50", landing: landingKey, session_id: sessionId }).catch(() => {});
      }
      if (pct >= 90 && !window.__vl_s90) { window.__vl_s90 = true;
        axios.post(`${API}/public/landing/track`, { event_type: "scroll_90", landing: landingKey, session_id: sessionId }).catch(() => {});
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line
  }, []);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name.trim() || !leadForm.phone.trim()) {
      toast.error(language === "it" ? "Inserisci nome e telefono" : "Enter name and phone");
      return;
    }
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads/submit`, {
        name: leadForm.name,
        phone: leadForm.phone,
        sector: leadForm.sector,
        message: leadForm.message,
        source_page: window.location.pathname
      });
      axios.post(`${API}/public/landing/track`, {
        event_type: "lead_submit", landing: landingKey, session_id: sessionId,
      }).catch(() => {});
      setLeadSubmitted(true);
      toast.success(language === "it" ? "Richiesta inviata! Ti contattiamo a breve." : "Request sent! We'll contact you shortly.");
    } catch {
      toast.error(language === "it" ? "Errore, riprova" : "Error, try again");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white overflow-hidden">

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <img src="/brand/sb-icon.png" alt="SmartBooker" className="h-9 w-9 drop-shadow-[0_0_12px_rgba(139,92,246,0.25)]" />
              <span className="font-semibold text-xl text-slate-900 dark:text-white hidden sm:inline">SmartBooker</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#problemi" className="text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">{language === "it" ? "Problemi" : "Problems"}</a>
              <a href="#soluzione" className="text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">{language === "it" ? "Soluzione" : "Solution"}</a>
              <a href="#come-funziona" className="text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">{language === "it" ? "Come Funziona" : "How It Works"}</a>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => changeLanguage(language === "it" ? "en" : "it")} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition-colors" data-testid="language-toggle">
                <Globe className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
              <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition-colors" data-testid="theme-toggle">
                {theme === "light" ? <Moon className="w-5 h-5 text-slate-500 dark:text-slate-400" /> : <Sun className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
              </button>
              {user ? (
                <Link to="/dashboard">
                  <Button className="rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 font-semibold px-5" data-testid="dashboard-btn">{t("dashboard")}</Button>
                </Link>
              ) : (
                <Link to="/login">
                  <Button className="rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 font-semibold px-5" data-testid="login-btn">{t("login")}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-[600px] h-[600px] rounded-full blur-[120px]" style={{ backgroundColor: `${sector.accent}08` }} />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-500/8 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8" style={{ backgroundColor: `${sector.accent}15`, borderColor: `${sector.accent}30` }}>
            <sector.icon className="w-4 h-4" style={{ color: sector.accent }} />
            <span className="text-sm font-medium" style={{ color: sector.accent }}>{s.badge}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.1] tracking-tight" data-testid="vertical-hero-headline">
            <span className="text-slate-900 dark:text-white">{s.headline1}</span><br />
            <span style={{ color: sector.accent }}>{s.headline2}</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mt-8 leading-relaxed">{s.subheadline}</p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link to="/register">
              <Button size="lg" className="w-full sm:w-auto rounded-full px-10 py-6 text-lg font-bold shadow-xl transition-all hover:scale-105 text-slate-950" style={{ backgroundColor: sector.accent }} data-testid="vertical-cta-primary">
                {s.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to={
              sector.sectorKey === "estetica" ? "/demo-estetica" :
              sector.sectorKey === "barbiere" ? "/demo-hair" :
              sector.sectorKey === "dentisti" ? "/demo-odonto" :
              sector.sectorKey === "fisioterapia" ? "/demo-benessere-posturale" :
              "/book/demo-salon"
            }>
              <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-2 border-white/15 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 px-10 py-6 text-lg transition-all" data-testid="vertical-cta-demo">
                <Play className="mr-2 w-5 h-5" />
                {language === "it" ? "Vedi demo" : "See demo"}
              </Button>
            </Link>
            {sector.brochure?.[language] && (
              <a
                href={sector.brochure[language].url}
                download={sector.brochure[language].filename}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  try {
                    if (window.gtag) window.gtag("event", "brochure_download", { vertical: sector.sectorKey, lang: language });
                  } catch { /* noop */ }
                }}
                data-testid="vertical-cta-brochure"
              >
                <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full border-2 border-amber-400/30 text-slate-700 dark:text-white hover:bg-amber-50 dark:hover:bg-amber-500/10 px-10 py-6 text-lg transition-all">
                  <Download className="mr-2 w-5 h-5" />
                  {sector.brochure[language].label}
                </Button>
              </a>
            )}
          </div>

          <div className="flex items-center justify-center gap-6 mt-10">
            <div className="flex -space-x-2">
              {s.avatars.map((a, i) => (
                <div key={i} className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-slate-950" style={{ backgroundColor: a.color }}>{a.initials}</div>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
            </div>
            <span className="text-sm text-slate-500">{s.trustLine}</span>
          </div>
        </div>
      </section>

      {/* PROBLEMI */}
      <section id="problemi" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium mb-4">
              {language === "it" ? "Il problema" : "The problem"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white" data-testid="vertical-problem-title">{s.problemTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {s.problems.map((p, i) => {
              const icons = [AlertTriangle, Clock, CircleDollarSign, BarChart3];
              const colors = ["border-red-500/20 bg-red-500/5", "border-orange-500/20 bg-orange-500/5", "border-amber-500/20 bg-amber-500/5", "border-violet-500/20 bg-violet-500/5"];
              const Icon = icons[i % icons.length];
              return (
                <div key={i} className={`p-6 rounded-2xl border ${colors[i % colors.length]} transition-all hover:scale-[1.02]`}>
                  <Icon className="w-8 h-8 text-slate-500 dark:text-slate-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-emerald-400 mb-2">{p.title}</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLUZIONE */}
      <section id="soluzione" className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${sector.accent}05, transparent)` }} />
        <div className="relative max-w-5xl mx-auto text-center">
          <span className="inline-block px-4 py-2 rounded-full border text-sm font-medium mb-6" style={{ backgroundColor: `${sector.accent}15`, borderColor: `${sector.accent}30`, color: sector.accent }}>
            {language === "it" ? "La soluzione" : "The solution"}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 leading-tight" data-testid="vertical-solution-title">{s.solutionTitle}</h2>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-16">{s.solutionDesc}</p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: CalendarCheck, title: s.sol1Title, desc: s.sol1Desc, highlight: s.sol1Highlight },
              { icon: Repeat, title: s.sol2Title, desc: s.sol2Desc, highlight: s.sol2Highlight },
              { icon: TrendingUp, title: s.sol3Title, desc: s.sol3Desc, highlight: s.sol3Highlight },
            ].map((item, i) => (
              <div key={i} className="relative p-8 rounded-2xl bg-white dark:bg-white/3 border border-white/8 hover:border-white/20 transition-all group" style={{ '--hover-color': `${sector.accent}30` }}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors" style={{ backgroundColor: `${sector.accent}15` }}>
                  <item.icon className="w-7 h-7" style={{ color: sector.accent }} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-emerald-400 mb-3">{item.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-4">{item.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: sector.accent }}>
                  <ArrowUpRight className="w-4 h-4" />
                  {item.highlight}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">{s.benefitsTitle}</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-4 text-lg">{s.benefitsSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {s.benefits.map((b, i) => {
              const icons = [CircleDollarSign, Users, Clock, Zap, Sparkles, CalendarCheck];
              const Icon = icons[i % icons.length];
              return (
                <div key={i} className="p-6 rounded-2xl border border-slate-200 dark:border-white/5 hover:border-white/15 transition-all group">
                  <Icon className="w-8 h-8 text-slate-500 mb-4 group-hover:text-emerald-600 dark:group-hover:text-white transition-colors" style={{ '--hover-color': sector.accent }} />
                  <h3 className="font-semibold text-slate-900 dark:text-emerald-400 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AUTOMAZIONI */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full border text-sm font-medium mb-4" style={{ backgroundColor: `${sector.accent}15`, borderColor: `${sector.accent}30`, color: sector.accent }}>
              {language === "it" ? "Automazioni" : "Automations"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {language === "it" ? "Il sistema lavora per te, 24/7" : "The system works for you, 24/7"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MessageSquare, title: language === "it" ? "WhatsApp Automatico" : "Auto WhatsApp", desc: language === "it" ? "Risposte, promemoria e follow-up automatici via WhatsApp. Nessun messaggio perso." : "Automatic replies, reminders, and follow-ups via WhatsApp. No message lost." },
              { icon: CreditCard, title: language === "it" ? "Pagamenti Online" : "Online Payments", desc: language === "it" ? "Acconti e pagamenti integrati. I clienti pagano al momento della prenotazione." : "Integrated deposits and payments. Clients pay at the time of booking." },
              { icon: Users, title: language === "it" ? "CRM Automatico" : "Auto CRM", desc: language === "it" ? "Ogni cliente tracciato automaticamente: visite, spesa, preferenze, storico completo." : "Every client tracked automatically: visits, spending, preferences, complete history." },
              { icon: Wallet, title: language === "it" ? "Analisi Valore Cliente" : "Client Value Analysis", desc: language === "it" ? "LTV automatico. Sai quanto vale ogni cliente e come farlo tornare." : "Automatic LTV. Know what each client is worth and how to bring them back." },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl border border-white/8 bg-white dark:bg-white/3 hover:bg-white/5 transition-all">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${sector.accent}15` }}>
                  <item.icon className="w-6 h-6" style={{ color: sector.accent }} />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-emerald-400 mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-white/[0.02]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {language === "it" ? "3 passi. Il tuo business cresce." : "3 steps. Your business grows."}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: s.step1Title, desc: s.step1Desc },
              { step: "02", title: s.step2Title, desc: s.step2Desc },
              { step: "03", title: s.step3Title, desc: s.step3Desc },
            ].map((item, i) => (
              <div key={i} className="relative">
                {i < 2 && <div className="hidden md:block absolute top-12 right-0 w-full h-px bg-gradient-to-r from-white/10 to-transparent translate-x-1/2" />}
                <div className="relative p-8 rounded-2xl border border-white/8 hover:border-white/20 transition-all">
                  <span className="text-5xl font-bold text-white/5 absolute top-4 right-6">{item.step}</span>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${sector.accent}15` }}>
                    <span className="text-lg font-bold" style={{ color: sector.accent }}>{item.step}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-emerald-400 mb-3">{item.title}</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      {s.testimonial && (
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="relative p-8 md:p-10 rounded-2xl border border-white/8 bg-white dark:bg-white/3">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-6 italic">"{s.testimonial.text}"</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-slate-900 dark:text-white" style={{ backgroundColor: sector.accent }}>{s.testimonial.avatar}</div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-emerald-400 text-sm">{s.testimonial.name}</p>
                    <p className="text-xs text-slate-500 dark:text-emerald-400/70">{s.testimonial.role}</p>
                  </div>
                </div>
                <span className="font-bold text-sm" style={{ color: sector.accent }}>{s.testimonial.metric}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SMART RECORDS — Premium feature highlight (NEW) */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-violet-50 via-white to-cyan-50 dark:from-violet-950/30 dark:via-slate-950 dark:to-cyan-950/30" id="smart-records" data-testid="vertical-smart-records-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] mb-4 shadow-md">
                <Crown className="w-3 h-3" /> {language === "it" ? "Smart Records · Premium" : "Smart Records · Premium"}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white leading-tight">
                {language === "it" ? "Ogni cliente, una scheda intelligente completa." : "Every client, a complete smart record."}
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mt-4 text-lg leading-relaxed">
                {language === "it"
                  ? `SmartBooker non è solo agenda. È il sistema operativo del tuo business: ${(sector.recordsHighlights_it || ["anamnesi", "trattamenti", "documenti", "preventivi", "timeline cliente", "AI summary"]).slice(0, 6).join(" · ")}.`
                  : `SmartBooker is not just a calendar. It's the operating system for your business: history, treatments, documents, quotes, client timeline, AI summary.`}
              </p>
              <ul className="mt-6 space-y-3">
                {(language === "it" ? (sector.recordsHighlights_it || ["Anamnesi e cure", "Trattamenti e storico", "Documenti clinici", "Preventivi e pacchetti", "Timeline cliente completa", "AI summary settoriale"]) : (sector.recordsHighlights_en || ["History & treatments", "Procedures & log", "Clinical documents", "Quotes & packages", "Full client timeline", "Sector-aware AI summary"])).slice(0, 6).map((h) => (
                  <li key={h} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </span>
                    <span className="text-sm text-slate-700 dark:text-slate-200">{h}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to={
                  sector.sectorKey === "estetica" ? "/demo-estetica" :
                  sector.sectorKey === "barbiere" ? "/demo-hair" :
                  sector.sectorKey === "dentisti" ? "/demo-odonto" :
                  sector.sectorKey === "fisioterapia" ? "/demo-benessere-posturale" :
                  "/demo"
                }>
                  <Button className="rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-700 hover:to-cyan-600 text-white shadow-lg" data-testid="vertical-smart-records-cta" id="landing-sr-cta">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {language === "it" ? "Vedi le Smart Records in azione" : "See Smart Records in action"}
                  </Button>
                </Link>
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <Shield className="w-3 h-3" /> {language === "it" ? "GDPR-ready · Audit log incluso" : "GDPR-ready · Audit log included"}
                </span>
              </div>
            </div>

            {/* Mockup card preview */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-violet-400/20 to-cyan-400/20 blur-3xl rounded-full" />
              <div className="relative rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-red-300" />
                  <span className="w-2 h-2 rounded-full bg-amber-300" />
                  <span className="w-2 h-2 rounded-full bg-emerald-300" />
                </div>
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100 dark:border-white/5">
                  <div className="w-12 h-12 rounded-2xl text-white flex items-center justify-center font-black text-sm shadow" style={{ background: `linear-gradient(135deg, ${sector.accent}, ${sector.accent}cc)` }}>
                    {(sector.recordsClientName || "Cliente Demo").split(" ").map(p => p[0]).join("").slice(0,2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 dark:text-white truncate">{sector.recordsClientName || "Cliente Demo"}</p>
                    <p className="text-[10px] text-slate-500">{s.badge || (language === "it" ? "Cliente fidelizzato" : "Loyal client")}</p>
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 inline-flex items-center gap-1">
                    <Crown className="w-2.5 h-2.5" /> Premium
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {(sector.recordsStats || [
                    { label: "Speso", value: "€820" },
                    { label: "Visite", value: "12" },
                    { label: "Ultima", value: "3 gg" },
                  ]).map(s => (
                    <div key={s.label} className="rounded-xl bg-slate-50 dark:bg-white/5 p-2 text-center">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{s.label}</p>
                      <p className="text-sm font-black text-slate-900 dark:text-white">{s.value}</p>
                    </div>
                  ))}
                </div>
                <ul className="mt-3 space-y-1.5">
                  {(sector.recordsTimeline || ["Trattamento completato", "Documento clinico caricato", "AI Summary aggiornato"]).slice(0, 3).map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-[11px]">
                      <span className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${sector.accent}20` }}>
                        <ClipboardList className="w-2.5 h-2.5" style={{ color: sector.accent }} />
                      </span>
                      <span className="text-slate-700 dark:text-slate-200 truncate">{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 rounded-xl bg-gradient-to-r from-violet-50 to-cyan-50 dark:from-violet-950/30 dark:to-cyan-950/20 border border-violet-200/60 p-2.5 flex items-start gap-2">
                  <Sparkles className="w-3 h-3 text-violet-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-slate-700 dark:text-slate-200">
                    <span className="font-bold text-violet-700 dark:text-violet-300">AI · </span>
                    {sector.recordsAiHint || (language === "it" ? "Cliente attivo, frequenza in linea. Programmare prossimo follow-up." : "Active client. Schedule next follow-up.")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-white/[0.02]" id="pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: `${sector.accent}15`, color: sector.accent, border: `1px solid ${sector.accent}30` }}>
              {language === "it" ? "Piani e Prezzi" : "Plans & Pricing"}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {language === "it" ? "Scegli il piano giusto per te" : "Choose the right plan for you"}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mt-4 text-lg max-w-xl mx-auto">
              {language === "it" ? "14 giorni gratis su ogni piano. Nessuna carta richiesta." : "14 days free on every plan. No card required."}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {(sector.sectorKey === "barbiere"
              ? [
                  {
                    name: "Essential", price: "34,95", annual: "349", desc: language === "it" ? "Per chi inizia" : "Getting started",
                    features: language === "it"
                      ? ["Prenotazioni 24/7", "CRM base", "Promemoria standard", "SmartPage white-label", "Dashboard base", "Pagamenti online"]
                      : ["24/7 bookings", "Basic CRM", "Standard reminders", "White-label SmartPage", "Basic dashboard", "Online payments"],
                  },
                  {
                    name: "Growth", price: "59,00", annual: "590", rec: true, desc: language === "it" ? "Per crescere davvero" : "For real growth",
                    features: language === "it"
                      ? ["Tutto di Essential +", "Smart Records · Fascicolo cliente AI", "WhatsApp automation", "Follow-up automatici", "Analytics LTV", "Suggerimenti AI"]
                      : ["Everything in Essential +", "Smart Records · AI client file", "WhatsApp automation", "Auto follow-ups", "LTV analytics", "AI suggestions"],
                  },
                  {
                    name: "Pro", price: "89,00", annual: "890", desc: language === "it" ? "Per dominare" : "For domination",
                    features: language === "it"
                      ? ["Tutto di Growth +", "Smart Records verticale settoriale", "WhatsApp campagne", "CRM scoring avanzato", "Dashboard avanzata", "Multi-sede (10)", "Supporto prioritario"]
                      : ["Everything in Growth +", "Vertical sector Smart Records", "WhatsApp campaigns", "Advanced CRM scoring", "Advanced dashboard", "Multi-location (10)", "Priority support"],
                  },
                ]
              : [
              {
                name: "Essential", price: "34,95", annual: "349", desc: language === "it" ? "Per chi inizia" : "Getting started",
                features: language === "it"
                  ? ["Prenotazioni 24/7", "CRM base", "Promemoria standard", "SmartPage white-label", "Dashboard base", "Pagamenti online"]
                  : ["24/7 bookings", "Basic CRM", "Standard reminders", "White-label SmartPage", "Basic dashboard", "Online payments"],
              },
              {
                name: "Growth", price: "89", annual: "890", rec: true, desc: language === "it" ? "Per crescere davvero" : "For real growth",
                features: language === "it"
                  ? ["Tutto di Essential +", "Smart Records · Fascicolo cliente AI", "WhatsApp automation", "Follow-up automatici", "Analytics LTV", "Suggerimenti AI"]
                  : ["Everything in Essential +", "Smart Records · AI client file", "WhatsApp automation", "Auto follow-ups", "LTV analytics", "AI suggestions"],
              },
              {
                name: "Pro", price: "179", annual: "1.790", desc: language === "it" ? "Per dominare" : "For domination",
                features: language === "it"
                  ? ["Tutto di Growth +", "Smart Records verticale settoriale", "WhatsApp campagne", "CRM scoring avanzato", "Dashboard avanzata", "Multi-sede (10)", "Supporto prioritario"]
                  : ["Everything in Growth +", "Vertical sector Smart Records", "WhatsApp campaigns", "Advanced CRM scoring", "Advanced dashboard", "Multi-location (10)", "Priority support"],
              },
            ]).map((tier, idx) => (
              <div key={idx} className={`relative rounded-2xl p-6 flex flex-col ${tier.rec ? "border-2 bg-emerald-50 dark:bg-slate-900" : "border border-slate-200 dark:border-white/10 bg-white dark:bg-white/3"}`} style={tier.rec ? { borderColor: `${sector.accent}50` } : {}} data-testid={`vertical-pricing-${tier.name.toLowerCase()}`}>
                {tier.rec && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-slate-950" style={{ backgroundColor: sector.accent }}>
                    {language === "it" ? "Il più Acquistato" : "Most purchased"}
                  </div>
                )}
                <h3 className={`text-lg font-bold text-slate-900 dark:text-emerald-400 ${tier.rec ? "mt-1" : ""}`}>{tier.name}</h3>
                <p className="text-xs text-slate-500 mb-3">{tier.desc}</p>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className={`text-3xl font-bold ${tier.rec ? "" : "text-slate-900 dark:text-emerald-400"}`} style={tier.rec ? { color: sector.accent } : {}}>€{tier.price}</span>
                  <span className="text-sm text-slate-500">/{language === "it" ? "mese" : "month"}</span>
                </div>
                <p className="text-xs text-slate-500 mb-4">€{tier.annual}/{language === "it" ? "anno" : "year"}</p>
                <div className="space-y-2 flex-grow">
                  {tier.features.map((f, i) => {
                    const isSmart = /Smart Records/i.test(f);
                    return (
                      <div key={i} className={`flex items-start gap-2 ${isSmart ? "rounded-lg bg-gradient-to-r from-violet-50 to-cyan-50 dark:from-violet-950/30 dark:to-cyan-950/20 border border-violet-200/60 dark:border-violet-900/30 px-2 py-1.5 -mx-1 shadow-sm" : ""}`}>
                        {isSmart ? (
                          <Crown className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-violet-600 dark:text-violet-300" />
                        ) : (
                          <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: sector.accent }} />
                        )}
                        <span className={`text-sm ${i === 0 && idx > 0 ? "font-medium" : ""} ${isSmart ? "font-semibold text-slate-800 dark:text-slate-100" : "text-slate-600 dark:text-slate-300"}`}>
                          {f}
                          {isSmart && (
                            <span className="ml-1.5 inline-flex items-center text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-600 text-white align-middle">PREMIUM</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6">
                  <Link to="/onboarding">
                    <Button size="lg" className={`w-full rounded-full py-5 font-bold ${tier.rec ? "text-slate-950 shadow-lg" : "border border-slate-300 dark:border-white/15 bg-transparent text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5"}`} style={tier.rec ? { backgroundColor: sector.accent } : {}}>
                      {language === "it" ? "Inizia gratis" : "Start free"}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE FORM */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-white/[0.02]" id="contatto">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/3 p-8 md:p-12 backdrop-blur-sm">
            {leadSubmitted ? (
              <div className="text-center py-8" data-testid="lead-form-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${sector.accent}20` }}>
                  <CheckCircle2 className="w-8 h-8" style={{ color: sector.accent }} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                  {language === "it" ? "Richiesta ricevuta!" : "Request received!"}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-lg">
                  {language === "it" 
                    ? "Un nostro consulente ti contatter\u00e0 entro poche ore per mostrarti come SmartBooker pu\u00f2 far crescere il tuo business."
                    : "One of our consultants will contact you within a few hours to show you how SmartBooker can grow your business."}
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">
                    {language === "it" ? "Vuoi sapere come funziona per te?" : "Want to know how it works for you?"}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === "it" 
                      ? "Lascia i tuoi dati. Ti mostriamo come SmartBooker pu\u00f2 aumentare il tuo fatturato."
                      : "Leave your details. We'll show you how SmartBooker can increase your revenue."}
                  </p>
                </div>
                <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="lead-capture-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5">{language === "it" ? "Nome *" : "Name *"}</label>
                      <Input
                        type="text"
                        value={leadForm.name}
                        onChange={(e) => setLeadForm(p => ({ ...p, name: e.target.value }))}
                        placeholder={language === "it" ? "Il tuo nome" : "Your name"}
                        className="bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 h-12 rounded-xl focus:border-emerald-500 dark:focus:border-white/30"
                        data-testid="lead-input-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5">{language === "it" ? "Telefono *" : "Phone *"}</label>
                      <Input
                        type="tel"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+39 320 000 0000"
                        className="bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 h-12 rounded-xl focus:border-emerald-500 dark:focus:border-white/30"
                        data-testid="lead-input-phone"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5">{language === "it" ? "Settore" : "Sector"}</label>
                    <select
                      value={leadForm.sector}
                      onChange={(e) => setLeadForm(p => ({ ...p, sector: e.target.value }))}
                      className="w-full h-12 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white px-4 focus:outline-none focus:border-emerald-500 dark:focus:border-white/30 appearance-none"
                      data-testid="lead-select-sector"
                    >
                      {SECTOR_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value} className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
                          {language === "it" ? opt.label_it : opt.label_en}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                      {language === "it" ? "Messaggio (opzionale)" : "Message (optional)"}
                    </label>
                    <textarea
                      value={leadForm.message}
                      onChange={(e) => setLeadForm(p => ({ ...p, message: e.target.value }))}
                      placeholder={language === "it" ? "Raccontaci il tuo business..." : "Tell us about your business..."}
                      rows={3}
                      className="w-full rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white px-4 py-3 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:outline-none focus:border-emerald-500 dark:focus:border-white/30 resize-none"
                      data-testid="lead-input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={leadSubmitting}
                    className="w-full rounded-xl py-6 text-lg font-bold text-slate-950 transition-all hover:scale-[1.02]"
                    style={{ backgroundColor: sector.accent }}
                    data-testid="lead-submit-btn"
                  >
                    {leadSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    {language === "it" ? "Richiedi informazioni" : "Request information"}
                  </Button>
                  <p className="text-xs text-slate-600 text-center">
                    {language === "it" 
                      ? "Nessun impegno. Ti contattiamo per una consulenza gratuita."
                      : "No commitment. We'll contact you for a free consultation."}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl p-12 md:p-16 text-center" style={{ background: `linear-gradient(135deg, ${sector.accent}, ${sector.accentDark})` }}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4 whitespace-pre-line" data-testid="vertical-final-cta">{s.finalCtaTitle}</h2>
              <p className="text-slate-600 dark:text-white/80 text-lg mb-8 max-w-xl mx-auto">{s.finalCtaDesc}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-lg font-bold shadow-xl hover:scale-105 transition-all">
                    {s.finalCtaButton}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <p className="text-white/50 text-sm mt-6">{language === "it" ? "Prova gratuita. Nessuna carta richiesta." : "Free trial. No card required."}</p>
            </div>
          </div>
        </div>
      </section>

      {/* BACK TO MAIN */}
      <section className="pb-16 px-4 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors text-sm">
          <ArrowRight className="w-4 h-4 rotate-180" />
          {language === "it" ? "Scopri tutte le funzionalit\u00e0 di SmartBooker" : "Discover all SmartBooker features"}
        </Link>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <img src="/brand/sb-icon.png" alt="SmartBooker" className="h-8 w-8 drop-shadow-[0_0_8px_rgba(139,92,246,0.25)]" />
            <span className="font-semibold text-slate-900 dark:text-white">SmartBooker</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-500">
            <Link to="/privacy-policy" className="hover:text-slate-900 dark:hover:text-white transition-colors">Privacy</Link>
            <Link to="/cookie-policy" className="hover:text-slate-900 dark:hover:text-white transition-colors">Cookie</Link>
            <Link to="/terms-conditions" className="hover:text-slate-900 dark:hover:text-white transition-colors">{language === "it" ? "Termini" : "Terms"}</Link>
            <Link to="/dpa" className="hover:text-slate-900 dark:hover:text-white transition-colors">DPA</Link>
            <a href="mailto:info@smartbooker.it" className="hover:text-slate-900 dark:hover:text-white transition-colors">info@smartbooker.it</a>
          </div>
          <p className="text-xs text-slate-600">© 2026 SmartBooker</p>
        </div>
      </footer>
    </div>
  );
};

export default VerticalLanding;
